import { Link } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import iconQualification from '../../assets/images/instructor-stat-qualification.png';
import iconExperience from '../../assets/images/instructor-stat-experience.png';
import iconVideos from '../../assets/images/instructor-stat-videos.png';

function parseHex(hex) {
  const raw = String(hex || '').replace('#', '').trim();
  if (!/^[0-9a-fA-F]{6}$/.test(raw)) return null;
  return [0, 2, 4].map((offset) => parseInt(raw.slice(offset, offset + 2), 16));
}

function darkenHex(hex, amount = 28) {
  const rgb = parseHex(hex);
  if (!rgb) return hex || '#002B80';

  return `#${rgb
    .map((value) =>
      Math.max(0, Math.min(255, value - amount))
        .toString(16)
        .padStart(2, '0')
    )
    .join('')}`;
}

function isLightColor(hex) {
  const rgb = parseHex(hex);
  if (!rgb) return false;
  const [r, g, b] = rgb;
  return (r * 299 + g * 587 + b * 114) / 1000 > 160;
}

function InstructorProfileSection({ instructor }) {
  const subjectColor = instructor.subjectColorCode || '#0076C0';
  // Keep subject hue intact; only a soft darken + light overlay for readable white text.
  const endColor = darkenHex(subjectColor, isLightColor(subjectColor) ? 40 : 28);
  const overlay = isLightColor(subjectColor)
    ? 'linear-gradient(90deg, rgba(0,0,0,0.22), rgba(0,0,0,0.32)), '
    : '';
  const sectionStyle = {
    backgroundImage: `${overlay}linear-gradient(90deg, ${subjectColor}, ${endColor})`,
  };

  return (
    <section
      className="h-[258px] max-h-[258px] overflow-hidden rounded-2xl"
      style={sectionStyle}
    >
      <div className="flex flex-col gap-6 p-6 sm:flex-row sm:items-start sm:p-8">
        <div className="relative shrink-0">
          <div className="flex h-28 w-28 items-center justify-center rounded-full bg-[#69991D] p-1 sm:h-[195px] sm:w-[135px]">
            <img
              src={instructor.profileImage}
              alt={instructor.name}
              className="h-full w-full rounded-full object-cover"
            />
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="p-2">
              <h2 className="text-xl font-semibold text-white sm:text-[20px]">{instructor.name}</h2>
              <p className="mt-1 text-[14px] font-medium tracking-wide text-[#CCCCCC]">{instructor.title}</p>
            </div>
            {instructor.id ? (
              <Link
                to={ROUTES.TEACHER_DETAIL(instructor.id)}
                className="rounded-lg border border-white/20 bg-black/20 px-4 py-2 text-sm font-semibold text-white transition hover:bg-black/30"
              >
                View Profile
              </Link>
            ) : (
              <button
                type="button"
                className="rounded-lg border border-white/20 bg-black/20 px-4 py-2 text-sm font-semibold text-white transition"
              >
                View Profile
              </button>
            )}
          </div>

          <div className="mt-3 flex flex-wrap gap-6 p-2">
            <InstructorStat icon={iconQualification} label={instructor.degree} />
            <InstructorStat icon={iconExperience} label={instructor.experience} />
            <InstructorStat icon={iconVideos} label={instructor.videos} />
          </div>

          <p className="mt-2 p-2 text-[14px] leading-relaxed text-[#CCCCCC]">{instructor.bio}</p>
        </div>
      </div>
    </section>
  );
}

function InstructorStat({ icon, label }) {
  return (
    <div className="flex items-center gap-2 text-white">
      <img src={icon} alt="" className="h-5 w-5 shrink-0 object-contain" aria-hidden />
      <span className="text-sm font-medium">{label}</span>
    </div>
  );
}

export default InstructorProfileSection;
